import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import { useFormik } from "formik"
import { string, object, array } from "yup"
import { useTranslation } from "react-i18next"
import { HiX } from "react-icons/hi"

import Header from "../../../components/header"
import Progressbar from "../../../components/progressbar"
import PostcodeSelect from "../../../components/postcodeSelect"
import SearchSelectMultiple from "../../../components/searchSelectMultiple"
import SearchSelect from "../../../components/searchSelect"
import Breadcrumb from "../../../components/breadcrumb"
import Tooltip from "../../../components/tooltip"
import ConfirmationModal from "../../../components/confirmation"
import { twClassNames } from "../../../helpers/classNames"
import { capitalize } from "../../../helpers/capitalize"
import { createActivity, fetchActivityCategories } from "../../../actions/activities"
import { fetchTeams } from "../../../actions/team"

const PHASES = 2
const breadcrumbs = [
  { name: "activities.create.breadcrumb.home", url: "/activities" },
  { name: "activities.create.breadcrumb.create" }
]

const CONFIRMATION_DEFAULT = {
  show: false,
  callback: null,
  danger: false,
  title: "",
  content: ""
}

const validationSchema = (phase, t) => {
  if (phase === 1) {
    return object().shape({
      streetName: string()
        .min(3, t("common.validation.invalidMinLength"))
        .max(120, t("common.validation.invalidMaxLength"))
        .required(t("common.validation.required")),
      postalCode: string().required(t("common.validation.required")),
      phone: string()
        .nullable()
        .min(6, t("common.validation.invalidMinLength"))
        .max(30, t("common.validation.invalidMaxLength"))
        .matches(/^\+(?:[0-9]?){6,14}[0-9]$/, t("common.validation.invalidPhone"))
    })
  } else if (phase === 2) {
    return object().shape(
      {
        nameFi: string()
          .when(["nameEn"], {
            is: nameEn => !nameEn,
            then: () => string().required(t("common.validation.eitherRequired"))
          })
          .min(3, t("common.validation.invalidMinLength"))
          .max(120, t("common.validation.invalidMaxLength")),
        nameEn: string()
          .when(["nameFi"], {
            is: nameFi => !nameFi,
            then: () => string().required(t("common.validation.eitherRequired"))
          })
          .min(3, t("common.validation.invalidMinLength"))
          .max(120, t("common.validation.invalidMaxLength")),
        descriptionFi: string().when(["descriptionEn"], {
          is: descriptionEn => !descriptionEn,
          then: () => string().required(t("common.validation.eitherRequired"))
        }),
        descriptionEn: string().when(["descriptionFi"], {
          is: descriptionFi => !descriptionFi,
          then: () => string().required(t("common.validation.eitherRequired"))
        }),
        categories: array().of(string()).required(t("common.validation.required"))
      },
      [
        ["nameFi", "nameEn"],
        ["descriptionFi", "descriptionEn"]
      ]
    )
  }
}

const Form1 = ({ teams, t, formik, isLoading, isValidCLass, onPrevious, onNext }) => (
  <div className="tw-flex tw-p-6 max-md:tw-flex-col">
    {/* Left side */}
    <div className="tw-min-w-[300px] tw-w-full md:tw-w-1/3 tw-mb-6 md:tw-border-r max-md:tw-border-b">
      {/* Host informations */}
      <div className="tw-relative md:tw-pr-6">
        <h5 className="tw-text-lg tw-mb-2">{t("activities.create.form.phase1.name")}</h5>
        <p className="tw-text-sm tw-text-dark-75">{t("activities.create.form.phase1.info")}</p>
      </div>
    </div>
    {/* Right side */}
    <div className="tw-w-full md:tw-w-2/3">
      <div className="tw-h-full tw-flex tw-flex-col md:tw-pl-6">
        <div className="tw-grid tw-grid-cols md:tw-grid-cols-6 tw-gap-x-2 tw-gap-y-3 md:tw-gap-y-4">
          {/* Team */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="ownerTeamId" className="tw-form-label">
              {t("handbook.create.form.team")}
            </label>
            <SearchSelect
              id="ownerTeamId"
              name="ownerTeamId"
              data={teams.map(team => ({
                name: team.name,
                id: team.id
              }))}
              nameKey="name"
              valueKey="id"
              indexKey="id"
              onChange={value => formik.setFieldValue("ownerTeamId", value)}
              value={formik.values.ownerTeamId}
              errors={formik.touched.ownerTeamId && formik.errors.ownerTeamId}
            />
          </div>
          {/* Host name */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="host" className="tw-form-label">
              {t("activities.create.form.host")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
              <Tooltip message="activities.create.tooltips.host" displayLabel />
            </label>
            <input
              type="text"
              id="host"
              name="host"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.host ?? ""}
              className={twClassNames(isValidCLass("host"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.host}</div>
          </div>
          {/* Street name */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="streetName" className="tw-form-label">
              {t("activities.create.form.streetName")}
            </label>
            <input
              type="text"
              id="streetName"
              name="streetName"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.streetName ?? ""}
              className={twClassNames(isValidCLass("streetName"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.streetName}</div>
          </div>
          {/* Postalcode */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="postalCode" className="tw-form-label">
              {t("activities.create.form.postalCode")}
            </label>
            <PostcodeSelect
              id="postalCode"
              name="postalCode"
              value={formik.values.postalCode}
              onBlur={formik.handleBlur}
              onChange={value => {
                formik.setFieldValue("city", value.city)
                formik.setFieldValue("postalCode", value.postcode)
              }}
              errors={formik.touched.postalCode && formik.errors.postalCode}
            />
          </div>
          {/* City */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="city" className="tw-form-label">
              {t("activities.create.form.city")}
              <Tooltip message="activities.create.tooltips.city" />
            </label>
            <input value={formik.values.city} className="tw-hidden tw-peer" readOnly />
            <p
              className="tw-block tw-w-full tw-pt-2 tw-px-3 tw-capitalize 
              tw-text-base sm:tw-text-sm peer-disabled:tw-text-dark-50 tw-value-field">
              {formik.values.city}
            </p>
          </div>
          {/* Phone */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="phone" className="tw-form-label">
              {t("activities.create.form.phone")}
              <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
              <Tooltip message="activities.create.tooltips.phone" />
            </label>
            <input
              type="phone"
              id="phone"
              name="phone"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.phone ?? ""}
              className={twClassNames(isValidCLass("phone"), "tw-form-control")}
            />
            <div className="tw-invalid-tooltip">{formik.errors.phone}</div>
          </div>
        </div>
        <div className="tw-flex tw-grow tw-place-content-between tw-items-end tw-pt-12">
          <button
            type="button"
            onClick={onPrevious}
            className="tw-btn tw-btn-outline tw-w-full tw-max-w-[180px]"
            disabled={isLoading}>
            {t("activities.create.button.cancel")}
          </button>
          <button
            type="button"
            onClick={onNext}
            className="tw-btn tw-btn-primary tw-w-full tw-max-w-[180px]"
            disabled={isLoading}>
            {t("activities.create.button.next")}
          </button>
        </div>
      </div>
    </div>
  </div>
)

const Form2 = ({ t, formik, isLoading, isValidCLass, onPrevious, onNext, categories, language = "en" }) => (
  <div className="tw-flex tw-p-6 max-md:tw-flex-col">
    {/* Left side */}
    <div className="tw-min-w-[300px] tw-w-full md:tw-w-1/3 tw-mb-6 md:tw-border-r max-md:tw-border-b">
      {/* Activities informations */}
      <div className="tw-relative md:tw-pr-6">
        <h5 className="tw-text-lg tw-mb-2">{t("activities.create.form.phase2.name")}</h5>
        <p className="tw-text-sm tw-text-dark-75">{t("activities.create.form.phase2.info")}</p>
      </div>
    </div>
    {/* Right side */}
    <div className="tw-w-full md:tw-w-2/3">
      <div className="tw-h-full tw-flex tw-flex-col md:tw-pl-6">
        <div className="tw-grid tw-grid-cols md:tw-grid-cols-6 tw-gap-x-2 tw-gap-y-3 md:tw-gap-y-4">
          {/* Name FI */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="nameFi" className="tw-form-label">
              {t("activities.create.form.nameFi")}
              <Tooltip message="activities.create.tooltips.nameFi" />
            </label>
            <input
              type="text"
              id="nameFi"
              name="nameFi"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nameFi}
              className={twClassNames(isValidCLass("nameFi"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.nameFi}</div>
          </div>
          {/* Name EN */}
          <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
            <label htmlFor="nameEn" className="tw-form-label tw-flex tw-justify-betweesn">
              {t("activities.create.form.nameEn")}
            </label>
            <input
              type="text"
              id="nameEn"
              name="nameEn"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.nameEn}
              className={twClassNames(isValidCLass("nameEn"), "tw-form-control")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.nameEn}</div>
          </div>
          {/* Description FI */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="descriptionFi" className="tw-form-label">
              {t("activities.create.form.descriptionFi")}
              <Tooltip message="activities.create.tooltips.descriptionFi" />
            </label>
            <textarea
              type="text"
              id="descriptionFi"
              name="descriptionFi"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.descriptionFi}
              className={twClassNames(isValidCLass("descriptionFi"), "tw-form-control tw-min-h-[110px]")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.descriptionFi}</div>
          </div>
          {/* Description EN */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="descriptionEn" className="tw-form-label">
              {t("activities.create.form.descriptionEn")}
            </label>
            <textarea
              type="text"
              id="descriptionEn"
              name="descriptionEn"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.descriptionEn}
              className={twClassNames(isValidCLass("descriptionEn"), "tw-form-control tw-min-h-[110px]")}
              spellCheck="false"
            />
            <div className="tw-invalid-tooltip">{formik.errors.descriptionEn}</div>
          </div>
          {/* Category */}
          <div className="tw-relative tw-col-span-6">
            <label htmlFor="categories" className="tw-form-label">
              {t("activities.create.form.categories")}
              <Tooltip message="activities.create.tooltips.categories" />
            </label>
            <SearchSelectMultiple
              id="categories"
              name="categories"
              data={categories}
              nameKey={"name" + capitalize(language)}
              valueKey="id"
              indexKey="id"
              placeholder={t("activities.create.placeholders.categories")}
              onChange={value => formik.setFieldValue("categories", value)}
              onBlur={formik.handleBlur}
              value={formik.values.categories}
              errors={formik.touched.categories && formik.errors.categories}
            />
          </div>
          <div className="tw-relative tw-col-span-6 tw-flex tw-overflow-x-auto">
            {formik.values.categories &&
              categories
                .filter(x => formik.values.categories.indexOf(x.id) !== -1)
                .map((tag, index) => (
                  <div
                    key={index}
                    className="tw-bg-dark-25 tw-text-dark tw-px-3 tw-py-1 tw-mr-1 tw-mb-2 
                tw-rounded tw-text-sm tw-cursor-default tw-min-w-fit">
                    {tag.name}
                    <HiX
                      className="tw-ml-1 tw-mb-0.5 tw-cursor-pointer tw-align-middle"
                      onClick={() =>
                        formik.setFieldValue(
                          "categories",
                          formik.values.categories.filter(x => x !== tag.id)
                        )
                      }
                    />
                  </div>
                ))}
          </div>
        </div>
        <div className="tw-flex tw-grow tw-place-content-between tw-items-end tw-pt-12">
          <button
            type="button"
            onClick={onPrevious}
            className="tw-btn tw-btn-outline tw-w-full tw-max-w-[180px]"
            disabled={isLoading}>
            {t("activities.create.button.previous")}
          </button>
          <button
            type="button"
            onClick={onNext}
            className="tw-btn tw-btn-primary tw-w-full tw-max-w-[180px]"
            disabled={isLoading}>
            {t("activities.create.button.save")}
          </button>
        </div>
      </div>
    </div>
  </div>
)

const CreateActivityContainer = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()

  const activityCategories = useSelector(state => state.activities.categories)
  const categoriesStatus = useSelector(state => state.activities.categoriesStatus)
  const isLoading = useSelector(state => state.activities.isLoading)
  const teams = useSelector(state => state.team.teams)

  const [phase, setPhase] = useState(1)
  const [confirmation, setConfirmation] = useState(CONFIRMATION_DEFAULT)

  useEffect(() => {
    if (categoriesStatus === "idle") {
      dispatch(fetchActivityCategories())
    }
  }, [categoriesStatus, dispatch])

  // Fetch teams
  useEffect(() => {
    dispatch(fetchTeams())
  }, [dispatch])

  const formik = useFormik({
    initialValues: {
      country: "finland",
      ownerTeamId: teams[0]?.id ?? ""
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validationSchema: validationSchema(phase, t),
    onSubmit: values => onSave(values)
  })

  const onSave = values => {
    const data = {
      ...values,
      name: values.nameFi || values.nameEn,
      description: values.descriptionFi || values.descriptionEn
    }
    dispatch(createActivity(data))
      .then(() => history.push("/activities/campaigns"))
      .catch(() => {})
  }

  const onNext = () => {
    formik.validateForm().then(errors => {
      formik.setErrors(errors)
      formik.setFieldTouched(Object.keys(errors), true)
      if (!Object.keys(errors).length) {
        if (phase === PHASES) {
          formik.handleSubmit()
        } else {
          setPhase(phase + 1)
        }
      }
    })
  }

  const onPrevious = () => {
    if (phase <= 1) {
      if (formik.dirty) {
        setConfirmation({
          show: true,
          callback: () => history.push("/activities"),
          title: "dirty",
          content: "dirty",
          danger: true
        })
      } else {
        history.push("/activities")
      }
    } else {
      setPhase(phase - 1)
    }
  }

  const isValidCLass = field => formik.touched[field] && formik.errors[field] && "tw-is-invalid"

  return (
    <>
      <Header />
      <div className="tw-has-header xl:tw-container tw-mx-auto tw-w-full tw-px-4 sm:tw-px-12">
        <Breadcrumb data={breadcrumbs} />
        <h1 className="tw-font-display tw-font-semibold tw-text-4xl tw-mb-6">{t("activities.create.title")}</h1>
        <div className="tw-mb-12 tw-w-full">
          <div className="tw-mb-6 tw-shadow tw-bg-white tw-rounded">
            <form noValidate onSubmit={formik.handleSubmit}>
              {(() => {
                switch (phase) {
                  case 1:
                    return (
                      <Form1
                        t={t}
                        teams={teams}
                        formik={formik}
                        isLoading={isLoading}
                        isValidCLass={isValidCLass}
                        onPrevious={onPrevious}
                        onNext={onNext}
                      />
                    )
                  case 2:
                    return (
                      <Form2
                        t={t}
                        formik={formik}
                        isLoading={isLoading}
                        isValidCLass={isValidCLass}
                        onPrevious={onPrevious}
                        onNext={onNext}
                        categories={activityCategories}
                        language={i18n.resolvedLanguage}
                      />
                    )
                  default:
                    return null
                }
              })()}
            </form>
          </div>
        </div>
        <div className="tw-mb-12 tw-w-full">
          <Progressbar phases={PHASES} current={phase} translationKeys="activities.create.progress" />
        </div>
        <ConfirmationModal
          show={confirmation.show}
          title={confirmation.title}
          content={confirmation.content}
          danger={confirmation.danger}
          onOK={confirmation.callback}
          onCancel={() => setConfirmation(CONFIRMATION_DEFAULT)}
        />
      </div>
    </>
  )
}

export default CreateActivityContainer
